import { Link, useNavigate } from 'react-router-dom';
import { CalendarRange, MapPin, Package2, Receipt, TrendingUp, XCircle, ChevronRight } from 'lucide-react';
import { Card } from '@/components/ui/Card';
import { Chip } from '@/components/ui/Chip';
import { Button } from '@/components/ui/Button';
import { EventActionMenu } from '@/components/events/EventActionMenu';
import { eventColorClasses } from '@/lib/eventColors';
import { cn, formatDateTime, formatIDR, formatNumber, formatRelative } from '@/lib/format';
import type { AppEvent } from '@/types/domain';

export interface EventCardProps {
  event: AppEvent;
  onClose?: (event: AppEvent) => void;
  onSelect?: (event: AppEvent) => void;
  onManageStock?: (event: AppEvent) => void;
  /** Hide the action button (used in history view). */
  hideActions?: boolean;
  /** Currently selected event id — renders a ring. */
  isActive?: boolean;
  /** Whether the viewer has permission to close events. */
  canClose?: boolean;
  /**
   * Whether to show the sales figure. Cashiers may not see what every event
   * has taken in total; they still see the transaction count, which is a
   * workload number rather than a money number.
   */
  showMoney?: boolean;
  onEdit?: (event: AppEvent) => void;
  onArchive?: (event: AppEvent) => void;
  onUnarchive?: (event: AppEvent) => void;
  onReopen?: (event: AppEvent) => void;
  onVoid?: (event: AppEvent) => void;
}

export const EVENT_STATUS_LABEL: Record<AppEvent['status'], string> = {
  active: 'Active',
  closed: 'Closed',
  archived: 'Diarsipkan',
  void: 'Void',
};

export const EVENT_STATUS_CHIP: Record<AppEvent['status'], 'active' | 'default' | 'danger'> = {
  active: 'active',
  closed: 'default',
  archived: 'default',
  void: 'danger',
};

export function EventCard({
  event,
  onClose,
  onSelect,
  onManageStock,
  hideActions = false,
  isActive = false,
  canClose = false,
  showMoney = true,
  onEdit,
  onArchive,
  onUnarchive,
  onReopen,
  onVoid,
}: EventCardProps) {
  const navigate = useNavigate();
  const isOpen = event.status === 'active';
  const color = eventColorClasses(event.color);

  return (
    <Card
      padding="md"
      hoverable={!!onSelect}
      className={cn('relative overflow-hidden', color.set && 'pl-8', isActive ? 'ring-2 ring-primary' : '')}
    >
      {/* A full-height colour bar down the left edge, and the code in a pill
          of the same colour. A 4px strip across the top was too easy to miss;
          this is the token the TopBar chip and the POS header carry, so the
          cashier can match them by eye. */}
      {color.set && <div className={cn('absolute inset-y-0 left-0 w-2', color.dot)} aria-hidden />}

      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="min-w-0 flex-1">
          <h3 className="text-subhead font-display font-semibold text-ink truncate">
            <Link to={`/events/${event.id}`} className="hover:underline">{event.name}</Link>
          </h3>
          <code
            className={cn(
              'mt-1 inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-caption font-mono',
              color.set ? cn(color.soft, color.text) : 'border-transparent px-0 text-ink-subtle',
            )}
          >
            {color.set && <span className={cn('w-2 h-2 rounded-full', color.dot)} aria-hidden />}
            {event.code}
          </code>
        </div>
        <Chip variant={EVENT_STATUS_CHIP[event.status]}>{EVENT_STATUS_LABEL[event.status]}</Chip>
      </div>

      <div className="space-y-1.5 text-caption text-ink-subtle">
        <p className="inline-flex items-center gap-1.5">
          <CalendarRange size={12} />
          <span>
            {formatDateTime(event.startAt)}
            {' · '}
            <span className="text-ink-subtle/70">{formatRelative(event.startAt)}</span>
          </span>
        </p>
        {event.endAt ? (
          <p className="inline-flex items-center gap-1.5">
            <XCircle size={12} />
            <span>
              Closed {formatDateTime(event.endAt)} · {formatRelative(event.endAt)}
            </span>
          </p>
        ) : null}
        {event.location && (
          <p className="inline-flex items-center gap-1.5">
            <MapPin size={12} />
            <span className="truncate max-w-[260px]">{event.location}</span>
          </p>
        )}
      </div>

      <div className={cn('mt-4 grid gap-3', showMoney ? 'grid-cols-2' : 'grid-cols-1')}>
        <Metric
          icon={<Receipt size={12} />}
          label="Transaksi"
          value={formatNumber(event.totalTransactions)}
        />
        {showMoney && (
          <Metric
            icon={<TrendingUp size={12} />}
            label="Penjualan"
            value={formatIDR(event.totalSales)}
          />
        )}
      </div>

      {!hideActions && (
        <div className="mt-4 pt-4 border-t border-line space-y-2">
          {onSelect && isOpen && (
            <Button
              variant={isActive ? 'primary' : 'secondary'}
              size="sm"
              fullWidth
              onClick={() => onSelect(event)}
            >
              {isActive ? 'Event aktif' : 'Pilih event ini'}
            </Button>
          )}
          <div className="flex items-center gap-2">
          {/* The way into the event: details, stock per lot, access, promos.
              A labelled button, because an icon-only row gave no hint that
              the event had a page at all. */}
          <Button
            variant="secondary"
            size="sm"
            className="flex-1"
            onClick={() => navigate(`/events/${event.id}`)}
            rightIcon={<ChevronRight size={14} />}
          >
            Detail event
          </Button>
          {onManageStock && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onManageStock(event)}
              aria-label="Stok event"
              title="Stok event"
              leftIcon={<Package2 size={16} className="text-ink-subtle" />}
            >
              Stok
            </Button>
          )}
          {isOpen && canClose && onClose && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onClose(event)}
              aria-label="Close event"
              title="Close event"
            >
              <XCircle size={16} className="text-danger" />
            </Button>
          )}
          <EventActionMenu
            event={event}
            onEdit={onEdit}
            onArchive={onArchive}
            onUnarchive={onUnarchive}
            onReopen={onReopen}
            onVoid={onVoid}
          />
          </div>
        </div>
      )}
    </Card>
  );
}

function Metric({
  icon,
  label,
  value,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
}) {
  return (
    <div className="p-2.5 rounded bg-canvas border border-line">
      <div className="inline-flex items-center gap-1 text-overline uppercase text-ink-subtle font-medium">
        {icon}
        {label}
      </div>
      <p className="text-body font-display font-semibold text-ink mt-1 truncate">{value}</p>
    </div>
  );
}
